import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StagingControlPage from './pages/StagingControlPage';
import StagingBackupsPage from './pages/StagingBackupsPage';
import DeploymentPage from './pages/DeploymentPage';
import LogsPage from './pages/LogsPage';
import BackupsPage from './pages/BackupsPage';
import SystemPage from './pages/SystemPage';
import UserManagementPage from './pages/UserManagementPage';
import {
  HiOutlineViewGrid, HiOutlineDocument, HiOutlineServer,
  HiOutlineDatabase, HiOutlineSun, HiOutlineMoon,
  HiOutlineLogout, HiOutlineUsers, HiOutlineUpload,
  HiOutlineChevronDown
} from 'react-icons/hi';
import packageJson from '../package.json';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Decode JWT payload (no verification — just extract claims)
function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch { return null; }
}

// Compact status badge for sidebar sections
function StatusBadge({ status }) {
  if (!status || status === 'unknown') return <span className="status-badge badge loading">…</span>;
  if (status === 'healthy') return <span className="status-badge badge healthy">UP</span>;
  if (status === 'degraded') return <span className="status-badge badge degraded">DEG</span>;
  return <span className="status-badge badge down">DOWN</span>;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('ds5_admin_token'));
  const [theme, setTheme] = useState(localStorage.getItem('ds5_admin_theme') || 'dark');
  const [health, setHealth] = useState({}); // { 'ds5-backend': 'healthy', ... }
  const [stagingStatus, setStagingStatus] = useState('unknown'); // 'healthy' | 'degraded' | 'down' | 'unknown'
  const intervalRef = useRef(null);

  // Decode user info from JWT
  const user = useMemo(() => {
    if (!token) return null;
    const decoded = decodeToken(token);
    if (!decoded) return null;
    return { username: decoded.username || 'admin', role: decoded.role || 'admin' };
  }, [token]);

  const isAdmin = user?.role === 'admin';

  // Accordion state for sidebar sections (persisted)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem('ds5_admin_collapsed');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('ds5_admin_collapsed', JSON.stringify(next));
      return next;
    });
  }, []);

  // Default view depends on role — restore last page on refresh
  const [activeView, _setActiveView] = useState(() => {
    const stored = localStorage.getItem('ds5_admin_token');
    if (stored) {
      const savedView = localStorage.getItem('ds5_admin_view');
      if (savedView) return savedView;
      const d = decodeToken(stored);
      return d?.role === 'validation' ? 'staging' : 'prod-overview';
    }
    return 'prod-overview';
  });

  // Wrap setActiveView to persist navigation
  const setActiveView = useCallback((view) => {
    _setActiveView(view);
    localStorage.setItem('ds5_admin_view', view);
  }, []);

  // Theme persistence
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ds5_admin_theme', theme);
  }, [theme]);

  const handleLogin = useCallback((newToken) => {
    localStorage.setItem('ds5_admin_token', newToken);
    localStorage.removeItem('ds5_admin_view'); // Fresh login → default page
    setToken(newToken);
    // Set default view based on role
    const decoded = decodeToken(newToken);
    setActiveView(decoded?.role === 'validation' ? 'staging' : 'prod-overview');
  }, [setActiveView]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('ds5_admin_token');
    localStorage.removeItem('ds5_admin_view');
    setToken(null);
  }, []);

  // Healthcheck polling — every 30s
  const fetchHealth = useCallback(async () => {
    if (!token) return;
    try {
      // Prod health
      const res = await fetch(`${API_BASE}/api/monitoring/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { handleLogout(); return; }
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data.services || []).forEach(s => { map[s.name] = s.status; });
        map['ds5-admin'] = 'healthy';
        setHealth(map);
      }

      // Staging status
      try {
        const sRes = await fetch(`${API_BASE}/api/staging/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (sRes.ok) {
          const sData = await sRes.json();
          const ec2State = sData.ec2?.state;
          if (ec2State === 'running') {
            const pm2Up = (sData.pm2 || []).some(p => p.status === 'online');
            setStagingStatus(pm2Up ? 'healthy' : 'degraded');
          } else if (ec2State === 'stopped') {
            setStagingStatus('down');
          } else if (ec2State === 'error') {
            setStagingStatus('degraded');
          } else {
            setStagingStatus('unknown');
          }
        }
      } catch { setStagingStatus('unknown'); }
    } catch {
      setHealth(prev => ({ ...prev, 'ds5-admin': 'down' }));
    }
  }, [token, handleLogout]);

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchHealth]);

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // Compute overall prod status from individual services
  const prodOverall = (() => {
    const svc = ['ds5-frontend', 'ds5-backend', 'ds5-suivi', 'ds5-admin'];
    const statuses = svc.map(s => health[s]);
    if (statuses.every(s => s === 'healthy')) return 'healthy';
    if (statuses.every(s => s === 'down')) return 'down';
    if (statuses.some(s => s === 'down' || s === 'degraded')) return 'degraded';
    return 'unknown';
  })();

  const prodMenu = isAdmin
    ? [
        { id: 'prod-overview', label: 'Overview', icon: <HiOutlineViewGrid className="nav-icon" /> },
        { id: 'prod-system', label: 'Système', icon: <HiOutlineServer className="nav-icon" /> },
        { id: 'prod-backups', label: 'Backups', icon: <HiOutlineDatabase className="nav-icon" /> },
        { id: 'prod-logs', label: 'Logs', icon: <HiOutlineDocument className="nav-icon" /> },
      ]
    : [
        { id: 'prod-overview', label: 'Overview', icon: <HiOutlineViewGrid className="nav-icon" /> },
        { id: 'prod-system', label: 'Système', icon: <HiOutlineServer className="nav-icon" /> },
      ];

  const stagingMenu = [
    { id: 'staging', label: 'Contrôle', icon: <HiOutlineServer className="nav-icon" /> },
    { id: 'staging-backups', label: 'Backups', icon: <HiOutlineDatabase className="nav-icon" /> },
    { id: 'staging-logs', label: 'Logs', icon: <HiOutlineDocument className="nav-icon" /> },
  ];

  const deployMenu = isAdmin
    ? [{ id: 'deployment', label: 'Déploiement', icon: <HiOutlineUpload className="nav-icon" /> }]
    : [];

  const adminMenu = isAdmin
    ? [{ id: 'admin-users', label: 'Utilisateurs', icon: <HiOutlineUsers className="nav-icon" /> }]
    : [];

  const renderContent = () => {
    // Guard: validation users can't access admin-only views
    if (!isAdmin && ['prod-logs', 'prod-backups', 'admin-users', 'deployment'].includes(activeView)) {
      setActiveView('staging');
      return null;
    }
    switch (activeView) {
      case 'prod-overview': return <DashboardPage token={token} role={user?.role} />;
      case 'prod-logs': return <LogsPage token={token} env="prod" role={user?.role} />;
      case 'prod-system': return <SystemPage token={token} />;
      case 'prod-backups': return <BackupsPage token={token} />;
      case 'staging': return <StagingControlPage token={token} />;
      case 'staging-backups': return <StagingBackupsPage token={token} stagingStatus={stagingStatus} onNavigate={setActiveView} />;
      case 'staging-logs': return <LogsPage token={token} env="staging" stagingStatus={stagingStatus} onNavigate={setActiveView} role={user?.role} />;
      case 'deployment': return isAdmin ? <DeploymentPage token={token} stagingStatus={stagingStatus} onNavigate={setActiveView} /> : null;
      case 'admin-users': return isAdmin ? <UserManagementPage token={token} /> : null;
      default: return <DashboardPage token={token} role={user?.role} />;
    }
  };

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>🔧 <span>DS5 Admin</span></h2>
          <button className="theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}>
            {theme === 'dark' ? <HiOutlineSun /> : <HiOutlineMoon />}
          </button>
        </div>

        {/* Welcome / profile section */}
        <div className="sidebar-profile">
          <div className="profile-avatar">{user?.username?.charAt(0).toUpperCase()}</div>
          <div className="profile-info">
            <span className="profile-name">{user?.username}</span>
            <span className={`role-badge ${user?.role}`}>
              {user?.role === 'admin' ? 'Admin' : 'Validation'}
            </span>
          </div>
        </div>

        <nav>
          <div className="nav-section">
            <div className="nav-section-label clickable" onClick={() => toggleSection('prod')}>
              🏭 Production <StatusBadge status={prodOverall} />
              <HiOutlineChevronDown className={`section-chevron ${collapsedSections.prod ? 'collapsed' : ''}`} />
            </div>
            <div className={`nav-section-items ${collapsedSections.prod ? 'collapsed' : ''}`}>
              {prodMenu.map(item => (
                <button
                  key={item.id}
                  className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                  onClick={() => setActiveView(item.id)}
                >
                  {item.icon} <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="nav-section" style={{ marginTop: 16 }}>
            <div className="nav-section-label clickable" onClick={() => toggleSection('staging')}>
              🧪 Staging <StatusBadge status={stagingStatus} />
              <HiOutlineChevronDown className={`section-chevron ${collapsedSections.staging ? 'collapsed' : ''}`} />
            </div>
            <div className={`nav-section-items ${collapsedSections.staging ? 'collapsed' : ''}`}>
              {stagingMenu.map(item => (
                <button
                  key={item.id}
                  className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                  onClick={() => setActiveView(item.id)}
                >
                  {item.icon} <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {(deployMenu.length > 0 || adminMenu.length > 0) && (
            <div className="nav-section" style={{ marginTop: 16 }}>
              <div className="nav-section-label clickable" onClick={() => toggleSection('admin')}>
                ⚙️ Administration
                <HiOutlineChevronDown className={`section-chevron ${collapsedSections.admin ? 'collapsed' : ''}`} />
              </div>
              <div className={`nav-section-items ${collapsedSections.admin ? 'collapsed' : ''}`}>
                {deployMenu.map(item => (
                  <button
                    key={item.id}
                    className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                    onClick={() => setActiveView(item.id)}
                  >
                    {item.icon} <span>{item.label}</span>
                  </button>
                ))}
                {adminMenu.map(item => (
                  <button
                    key={item.id}
                    className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                    onClick={() => setActiveView(item.id)}
                  >
                    {item.icon} <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="btn btn-outline" onClick={handleLogout}>
            <HiOutlineLogout /> Déconnexion
          </button>
          <div className="sidebar-footer-info">
            <span>v{packageJson.version}</span>
            <span>©{new Date().getFullYear()} MPSI - DS5 Tester</span>
            <span>Tous droits réservés</span>
          </div>
        </div>
      </aside>

      <main className="main-content fade-in" key={activeView}>
        <div className="layout">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default App;
